import { create } from 'zustand';
import { getSet, type ZikrSetId, type ResolvedZikr } from '@sayfazkari/content';
import type { ThemeMode } from '../theme/theme';
import { readJson, writeJson, writeRaw } from '../lib/storage';
import { cloudWrite } from '../telegram/cloudStorage';
import { recordCompletion, isDone } from '../lib/streak';
import { dayKey } from '../lib/time';

export type Screen = 'home' | 'session' | 'complete' | 'settings' | 'salawat';

interface Settings {
  themeMode: ThemeMode;
  arabicScale: number;
  showTranslit: boolean;
  showTranslation: boolean;
}

const SETTINGS_KEY = 'sa_settings_v1';
const PROGRESS_KEY = 'sa_progress_v1';

/**
 * Анти-флеш-скрипт в index.html читает режим темы ДО монтирования React,
 * поэтому ему нужен отдельный простой ключ. Раньше он его читал, но никто
 * не писал — выбранная тема терялась при каждом перезапуске.
 */
const THEME_MODE_KEY = 'sa_theme_mode';

const DEFAULT_SETTINGS: Settings = {
  themeMode: 'auto',
  arabicScale: 1,
  showTranslit: true,
  showTranslation: true,
};

interface Progress {
  /** День, которому принадлежит прогресс. Вчерашний не возобновляем. */
  day: string;
  activeSet: ZikrSetId | null;
  index: number;
  done: Record<string, number>;
}

const EMPTY_PROGRESS: Progress = {
  day: dayKey(),
  activeSet: null,
  index: 0,
  done: {},
};

function loadSettings(): Settings {
  const s = readJson<Partial<Settings>>(SETTINGS_KEY, {});
  return { ...DEFAULT_SETTINGS, ...s };
}

function loadProgress(): Progress {
  const p = readJson<Progress>(PROGRESS_KEY, EMPTY_PROGRESS);
  // Прогресс за прошлый день не возобновляется: азкары читают заново
  // каждый день, а не продолжают вчерашний набор
  return p.day === dayKey() ? p : { ...EMPTY_PROGRESS, day: dayKey() };
}

function persistSettings(s: Settings): void {
  writeJson(SETTINGS_KEY, s);
  writeRaw(THEME_MODE_KEY, s.themeMode);
  cloudWrite(SETTINGS_KEY, JSON.stringify(s));
}

function persistProgress(p: Progress): void {
  writeJson(PROGRESS_KEY, p);
  cloudWrite(PROGRESS_KEY, JSON.stringify(p));
}

interface SessionState {
  screen: Screen;
  activeSet: ZikrSetId | null;
  index: number;
  done: Record<string, number>;
  settings: Settings;
  /** Растёт при внешнем изменении (приход из облака) — чтобы UI перечитал стрик */
  revision: number;

  startSet: (id: ZikrSetId) => void;
  tap: () => boolean;
  goNext: () => void;
  goPrev: () => void;
  goToIndex: (i: number) => void;
  exitSession: () => void;
  openSettings: () => void;
  closeSettings: () => void;
  openSalawat: () => void;
  setSetting: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  hydrate: (p: Partial<Progress>, s: Partial<Settings>) => void;
  bumpRevision: () => void;
}

const initialProgress = loadProgress();

export const useSession = create<SessionState>((set, get) => ({
  screen: 'home',
  activeSet: initialProgress.activeSet,
  index: initialProgress.index,
  done: initialProgress.done,
  settings: loadSettings(),
  revision: 0,

  startSet: (id) => {
    const { activeSet, done, index } = get();
    // Возобновляем с места, если этот же набор уже начат сегодня
    const resuming = activeSet === id;
    const next: Progress = {
      day: dayKey(),
      activeSet: id,
      index: resuming ? index : 0,
      done: resuming ? done : {},
    };
    persistProgress(next);
    set({ screen: 'session', activeSet: id, index: next.index, done: next.done });
  },

  tap: () => {
    const { activeSet, index, done } = get();
    if (!activeSet) return false;

    const items = getSet(activeSet).items;
    const zikr = items[index];
    if (!zikr) return false;

    const current = done[zikr.id] ?? 0;
    if (current >= zikr.repeat) return false;

    const nextDone = { ...done, [zikr.id]: current + 1 };
    set({ done: nextDone });
    persistProgress({ day: dayKey(), activeSet, index, done: nextDone });
    return current + 1 >= zikr.repeat;
  },

  goNext: () => {
    const { activeSet, index, done } = get();
    if (!activeSet) return;
    const total = getSet(activeSet).items.length;

    if (index + 1 >= total) {
      // Набор дочитан — отмечаем день в истории для стрика
      recordCompletion(activeSet);
      persistProgress({ day: dayKey(), activeSet: null, index: 0, done: {} });
      set({ screen: 'complete', revision: get().revision + 1 });
      return;
    }

    const nextIndex = index + 1;
    persistProgress({ day: dayKey(), activeSet, index: nextIndex, done });
    set({ index: nextIndex });
  },

  goPrev: () => {
    const { index, activeSet, done } = get();
    if (index <= 0 || !activeSet) return;
    const nextIndex = index - 1;
    persistProgress({ day: dayKey(), activeSet, index: nextIndex, done });
    set({ index: nextIndex });
  },

  goToIndex: (i) => {
    const { activeSet, done } = get();
    if (!activeSet) return;
    const total = getSet(activeSet).items.length;
    const clamped = Math.max(0, Math.min(i, total - 1));
    persistProgress({ day: dayKey(), activeSet, index: clamped, done });
    set({ index: clamped });
  },

  exitSession: () => set({ screen: 'home' }),

  openSettings: () => set({ screen: 'settings' }),
  closeSettings: () => set({ screen: 'home' }),
  openSalawat: () => set({ screen: 'salawat' }),

  setSetting: (key, value) => {
    const next = { ...get().settings, [key]: value };
    persistSettings(next);
    set({ settings: next });
  },

  hydrate: (p, s) => {
    const cur = get();
    const settings = { ...cur.settings, ...s };
    set({ settings });
    writeJson(SETTINGS_KEY, settings);
    writeRaw(THEME_MODE_KEY, settings.themeMode);

    // Прогресс из облака берём, только если он за сегодня и в нём
    // больше сделано: иначе устройство с устаревшей копией откатило бы счёт
    if (p.day === dayKey() && p.activeSet) {
      const localCount = Object.values(cur.done).reduce((a, b) => a + b, 0);
      const cloudCount = Object.values(p.done ?? {}).reduce((a, b) => a + b, 0);
      if (cloudCount > localCount) {
        set({ activeSet: p.activeSet, index: p.index ?? 0, done: p.done ?? {} });
        writeJson(PROGRESS_KEY, p);
      }
    }
    set({ revision: cur.revision + 1 });
  },

  bumpRevision: () => set({ revision: get().revision + 1 }),
}));

/** Текущий азкар — null, если сессия не идёт */
export function useCurrentZikr(): ResolvedZikr | null {
  const activeSet = useSession((s) => s.activeSet);
  const index = useSession((s) => s.index);
  if (!activeSet) return null;
  return getSet(activeSet).items[index] ?? null;
}

/** Состояние набора на главном экране */
export function setStatus(id: ZikrSetId): 'done' | 'inProgress' | 'fresh' {
  if (isDone(id)) return 'done';
  const { activeSet, done } = useSession.getState();
  if (activeSet === id && Object.keys(done).length > 0) return 'inProgress';
  return 'fresh';
}

export { PROGRESS_KEY, SETTINGS_KEY };
